import React, { useState } from "react";
import { ChevronRight, Loader, X, MoreVertical, ArrowLeft } from "lucide-react";
import FieldOfWork from "./PaymentDetailsForm";
import { FaCheckSquare } from "react-icons/fa";
import axios from "axios";
import { BASE_URL } from "utils/constant";
import { useAlert } from "../../../components/UI_components/Alert";

const UnifiedForm = ({ type, onClose }) => {
  const alert = useAlert();
  const [step, setStep] = useState(1);
  const [view, setView] = useState("form"); // 'form' or 'fieldOfWork'
  const [errors, setErrors] = useState({});
  const [workerId, setWorkerId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaveClicked, setIsSaveClicked] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    fieldOfWork: "",
    workname_id: "",
    contact: "",
    address: "",
    paymentType: type === "contractor" ? "Contract" : "Daily wage",
    amount: "",
  });

  const isContractor = type === "contractor";
  const title = isContractor ? "New Contractor" : "New Worker";
  const nameLabel = isContractor ? "Leader's Name" : "Worker's Name";
  const contactLabel = isContractor
    ? "Leader's Contact number"
    : "Worker's Contact number";

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.workname_id) {
      newErrors.fieldOfWork = "Field of work is required";
    }
    if (!formData.contact || !/^\d{10}$/.test(formData.contact)) {
      newErrors.contact = "Valid 10-digit contact number is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = "Valid amount is required";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldSelect = (field) => {
    setFormData((prev) => ({
      ...prev,
      fieldOfWork: field.name,
      workname_id: field.id,
    }));
    setView("form");
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
    if (errors[id]) {
      setErrors((prev) => ({ ...prev, [id]: undefined }));
    }
  };

  const createWorker = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("access");
      const site = localStorage.getItem("site");
      const response = await axios.post(
        `${BASE_URL}/worker/create-worker/`,
        {
          types: isContractor ? "Crew" : "Worker",
          name: formData.name,
          contact: formData.contact,
          address: formData.address,
          sites: parseInt(site),
          workname_id: formData.workname_id,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.status) {
        setWorkerId(response.data.data.id);
        return true;
      } else {
        alert.addAlert(response.data.message, "error");
      }
      return false;
    } catch (error) {
      console.error("Failed to create worker:", error);
      setErrors({ submit: "Failed to create worker. Please try again." });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const createPayment = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem("access");
      const response = await axios.post(
        `${BASE_URL}/worker/create-workerpayment/`,
        {
          payment_type: formData.paymentType,
          amount: parseFloat(formData.amount),
          worker_id: workerId,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.status;
    } catch (error) {
      setErrors({ submit: "Failed to create payment details. Please try again." });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleNext = async (e) => {
    e.preventDefault();
    if (!validateStep1()) return;
    const success = await createWorker();
    if (success) {
      setStep(2);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isSaveClicked) return;
    setIsSaveClicked(true);
    if (!validateStep2()) {
      setIsSaveClicked(false);
      return;
    }
    const success = await createPayment();
    if (success) {
      alert.addAlert("Worker created successfully", "success");
      onClose();
    } else {
      setIsSaveClicked(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-[black] bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-2xl mx-auto rounded-2xl shadow-xl relative overflow-hidden">
        <div className="pt-2">
          {/* Dynamic Header */}
          <div className="w-full bg-white border-b">
            <div className="flex items-center justify-between px-4 py-2">
              <button
                onClick={view === 'form' ? onClose : () => setView('form')}
                className="text-gray-600 hover:text-black"
              >
                {view === 'form' ? <X className="h-5 w-5" /> : <ArrowLeft className="h-5 w-5" />}
              </button>
              <h2 className="text-[16px] font-medium text-[#232323]">
                {view === 'form' ? title : "Field of Work"}
              </h2>
              <div className="w-8">
                {view === 'form' && (
                  <button className="text-gray-600 hover:text-black">
                    <MoreVertical className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Body Container */}
          <div className="p-4">
            {view === 'form' ? (
              <>
                <div className="w-full bg-gray-200 h-1.5 rounded-full mb-4">
                  <div
                    className="bg-[#3B0069] h-1.5 rounded-full transition-all duration-300"
                    style={{ width: step === 1 ? "50%" : "100%" }}
                  />
                </div>
                {errors.submit && (
                  <div className="mb-4 p-2 bg-red-100 text-red-700 rounded">
                    {errors.submit}
                  </div>
                )}
                {step === 1 ? (
                  <form onSubmit={handleNext} className="space-y-5">
                    <div>
                      <h3 className="text-base font-bold mb-1 text-[#232323]">General Details</h3>
                      <p className="text-[14px] text-[#232323] mb-4">add Name, Field of Work, Address, Contact Number</p>
                    </div>
                    <div className="space-y-4 bg-white rounded-2xl p-4 shadow-sm">
                      <div className="space-y-2">
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700">{nameLabel}</label>
                        <input id="name" type="text" value={formData.name} onChange={handleInputChange} placeholder="Type" className={`mt-1 block w-full rounded-lg bg-[#F3F4F6] px-3 py-2 ${errors.name ? "border-red-500" : ""}`} />
                        {errors.name && <p className="text-red-500 text-sm">{errors.name}</p>}
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="fieldOfWork" className="block text-sm font-medium text-gray-700">Field of Work</label>
                        <button type="button" onClick={() => setView('fieldOfWork')} className={`mt-1 w-full rounded-lg px-3 py-2 bg-[#F3F4F6] flex items-center justify-between ${errors.fieldOfWork ? "border-red-500" : ""}`}>
                          <span className="text-left">{formData.fieldOfWork || "Select Field of Work"}</span>
                          <ChevronRight className="h-4 w-4 text-gray-500" />
                        </button>
                        {errors.fieldOfWork && <p className="text-red-500 text-sm">{errors.fieldOfWork}</p>}
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="contact" className="block text-sm font-medium text-gray-700">{contactLabel}</label>
                        <div className="flex">
                          <span className="inline-flex font-bold items-center px-3 py-2 text-sm text-gray-900 bg-[#F3F4F6] rounded-l-md">+91</span>
                          <input id="contact" type="tel" value={formData.contact} onChange={handleInputChange} placeholder="98700 00000" className={`flex-1 block rounded-r-md py-2 px-3 bg-[#F3F4F6] ${errors.contact ? "border-red-500" : ""}`} />
                        </div>
                        {errors.contact && <p className="text-red-500 text-sm">{errors.contact}</p>}
                      </div>
                      <div className="space-y-2">
                        <label htmlFor="address" className="block text-sm font-medium text-gray-700">Address (Optional)</label>
                        <input id="address" type="text" value={formData.address} onChange={handleInputChange} placeholder="Type" className="mt-1 block w-full rounded-lg bg-[#F3F4F6] px-3 py-2" />
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <button type="submit" disabled={isLoading} className="w-56 h-10 flex justify-center items-center border border-transparent rounded-md shadow-sm text-sm font-semibold text-white bg-[#3B0069] hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed">
                        {isLoading ? <><Loader className="h-5 w-5 animate-spin" /> Processing...</> : <>Next <ChevronRight className="ml-2 h-4 w-4" /></>}
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleSave} className="space-y-5">
                    <div>
                      <h3 className="text-base font-bold mb-1 text-gray-700">Payment Details</h3>
                      <p className="text-xs text-gray-800 mb-4">add details about the payment type and amounts</p>
                    </div>
                    <div className="space-y-4 bg-white rounded-2xl p-4 shadow-sm">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Payment Type</label>
                        <button type="button" className="w-full flex justify-between items-center px-4 py-2 bg-[#ECE6F0] text-gray-900 rounded-lg border-[1px] border-[#3B0069]">
                          {formData.paymentType}
                          <FaCheckSquare className="text-text-clr" />
                        </button>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Amount</label>
                        <div className="relative">
                          <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">₹</span>
                          <input type="number" id="amount" value={formData.amount} onChange={handleInputChange} placeholder="Amount" className={`pl-8 block w-full rounded-lg px-4 py-2 border-gray-300 bg-[#F3F4F6] ${errors.amount ? "border-red-500" : ""}`} />
                        </div>
                        {errors.amount && <p className="text-red-500 text-sm">{errors.amount}</p>}
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <button type="submit" disabled={isLoading || isSaveClicked} className="w-56 h-10 flex justify-center items-center border border-transparent rounded-md shadow-sm text-base font-bold text-white bg-[#3B0069] hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed">
                        {isLoading ? <><Loader className="h-5 w-5 animate-spin" /> Processing...</> : <>Save <ChevronRight className="ml-2 h-4 w-4" /></>}
                      </button>
                    </div>
                  </form>
                )}
              </>
            ) : (
              <FieldOfWork onSelect={handleFieldSelect} onClose={() => setView('form')} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default UnifiedForm;